import { createTheme } from '@mui/material/styles'
import colors from '../styles/colors'
import fonts from '../styles/fonts'
import palette from '../styles/palette'

/** `border/40` e `border/20` do mockup, em rgba do `palette.brand`. */
const brandBorder = 'rgba(106, 48, 222, 0.4)'
const brandBorderSoft = 'rgba(106, 48, 222, 0.2)'

/**
 * Tema MUI v5 derivado dos tokens do mockup Lovable.
 *
 * Só as folhas `sx`/componentes v5 leem este tema; as folhas `makeStyles`
 * (v4) continuam lendo `colors.ts` — ambos derivam de `palette.ts`, então
 * as duas camadas ficam coerentes.
 */
const theme = createTheme({
  palette: {
    primary: {
      main: palette.brand,
      dark: palette.brandDark,
      light: palette.brandSoft,
      contrastText: palette.brandForeground,
    },
    secondary: {
      main: palette.surface,
      contrastText: palette.brandDark,
    },
    error: { main: palette.danger, dark: palette.dangerText, light: palette.dangerSoft },
    warning: { main: palette.warning, light: palette.warningSoft },
    info: { main: palette.info, light: palette.infoSoft },
    success: { main: palette.emeraldDeep, light: palette.emeraldSoft },
    background: { default: palette.page, paper: palette.surface },
    text: { primary: palette.text, secondary: palette.textMuted, disabled: palette.textDisabled },
    divider: palette.border,
  },
  shape: {
    borderRadius: 8,
  },
  typography: {
    fontFamily: `"${fonts.barlow}", "${fonts.inter}", sans-serif`,
    // Escala do mockup: text-3xl / text-2xl / text-xl / text-lg / text-base.
    h1: { fontSize: '1.875rem', fontWeight: 600, lineHeight: 1.2 },
    h2: { fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.25 },
    h3: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.3 },
    h4: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.3 },
    h5: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.35 },
    h6: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.4 },
    subtitle1: { fontSize: '0.875rem', lineHeight: 1.5 },
    body1: { fontSize: '0.9375rem', lineHeight: 1.55 },
    body2: { fontSize: '0.875rem', lineHeight: 1.5 },
    button: { textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.025em' },
  },
  components: {
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: palette.radius,
          border: `1px solid ${palette.border}`,
          boxShadow: palette.shadowSm,
          backgroundImage: 'none',
          transition: 'box-shadow .18s ease, transform .18s ease',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: palette.radius },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          fontFamily: fonts.interComponent,
          fontWeight: 600,
          fontSize: '0.8125rem',
          letterSpacing: '0.025em',
          borderRadius: 6,
          paddingInline: 16,
        },
        outlined: {
          borderColor: brandBorder,
          '&:hover': { borderColor: palette.brand, backgroundColor: palette.brandSoft },
        },
        containedSecondary: {
          color: palette.brandDark,
          boxShadow: palette.shadowSm,
          '&:hover': { backgroundColor: palette.surface, boxShadow: palette.shadowMd },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: palette.brandDark, height: 2 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontSize: '0.875rem',
          fontWeight: 500,
          minHeight: 52,
          color: palette.textMuted,
          '&.Mui-selected': { color: palette.brandDark, fontWeight: 600 },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 6, fontWeight: 600 },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 999, backgroundColor: palette.brandSoft },
        bar: { borderRadius: 999, backgroundColor: palette.brand },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        track: { backgroundColor: palette.textDisabled, opacity: 1 },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: palette.border },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: palette.sidebar,
          fontSize: '0.75rem',
          borderRadius: 6,
        },
        arrow: { color: palette.sidebar },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& label': {
            color: palette.textMuted,
            fontFamily: fonts.interComponent,
          },
          '& label.Mui-focused': {
            color: palette.brand,
            fontFamily: fonts.interComponent,
          },
          '& .MuiInput-underline:after': {
            borderBottomColor: palette.brand,
            fontFamily: fonts.interComponent,
          },
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: palette.border,
              fontFamily: fonts.interComponent,
            },
            '&:hover fieldset': {
              borderColor: brandBorder,
              fontFamily: fonts.interComponent,
            },
            '&.Mui-focused fieldset': {
              borderColor: palette.brand,
              borderWidth: 1,
              fontFamily: fonts.interComponent,
            },
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          textTransform: 'initial',
          fontSize: '0.75rem',
          fontFamily: fonts.interComponent,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: colors.white,
          borderRadius: 6,
          fontFamily: fonts.interComponent,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: palette.border },
          '&:hover': {
            backgroundColor: colors.white,
            fontFamily: fonts.interComponent,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: brandBorderSoft },
            '@media (hover: none)': {
              backgroundColor: colors.white,
              fontFamily: fonts.interComponent,
            },
          },
          '&.Mui-focused': {
            backgroundColor: colors.white,
            fontFamily: fonts.interComponent,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: palette.brand,
              borderWidth: 1,
            },
          },
        },
      },
    },
  },
})

export default theme
